import { toast } from "$global-providers/toast-provider";
import { gql, useMutation } from "@apollo/client";
import { useCallback, useState } from "react";
import { useIntl } from "react-intl";

type TransformedTransaction = {
  payer: {
    type: string;
    name: string;
  };
  amount: {
    value: number;
    currency: string;
  };
  payment?: {
    paymentMethod: string;
    authCode?: string;
    amount?: {
      value: number;
      currency: string;
    };
  };
};

export const TAKE_PAYMENT = gql`
  mutation takePayment($input: TakePaymentInput!) {
    takePayment(input: $input) {
      _id
      invoiceStatus
      transactions {
        _id
        actionedBy {
          _id
          uuid
          role_id
          email
          person {
            firstname
            middlename
            lastname
          }
        }
        type
        status
        timestamp
        notes
        payer {
          type
          name
        }
        amount {
          value
          currency
        }
        refund {
          paymentMethod
          authCode
          amount {
            value
            currency
          }
          timestamp
          reason
        }
        payment {
          paymentMethod
          authCode
          amount {
            value
            currency
          }
        }
      }
      payers {
        balance {
          total
          paid
          due
        }
      }
      balance {
        total
        paid
        due
        currency
      }
    }
  }
`;

type Transaction = {
  type: string;
  amountInPence: number;
  method: string;
  authCode: string;
};

export const transformTransactions = (
  transactions: Transaction[],
  currency: string,
): TransformedTransaction[] => {
  return transactions.map((t) => ({
    payer: {
      type: t.type || "PATIENT",
      name: t.type || "patient",
    },
    amount: {
      value: t.amountInPence,
      currency,
    },
    payment:
      t.type === "WAIVER"
        ? undefined
        : {
            paymentMethod: t.method,
            authCode: t.authCode,
            amount: {
              value: t.amountInPence,
              currency,
            },
          },
  }));
};

export function useTakePayment(invoiceId: string) {
  const { formatMessage } = useIntl();
  const [saving, setSaving] = useState<boolean>(false);
  const [saved, setSaved] = useState<boolean>(false);

  const [makePayment] = useMutation(TAKE_PAYMENT, {
    onError(error) {
      toast.error(
        formatMessage(
          {
            id: "invoicing.takePayment.error.msg",
            defaultMessage: "Failed to record payment: {error}",
          },
          { error: error.message },
        ),
      );
    },
    onCompleted() {
      setSaving(false);
      setSaved(true);
    },
  });

  const takePayment = useCallback(
    async ({ transactions, currency }) => {
      setSaving(true);
      await makePayment({
        variables: {
          input: {
            _id: invoiceId,
            transactions: transformTransactions(transactions, currency),
          },
        },
      });
    },
    [makePayment, invoiceId],
  );

  return { takePayment, saving, saved };
}
